import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  changeNewPassword,
  changePassword,
  changePasswordAuth,
  forgotPassword,
  logOutFromAllDevices,
  publicSignUp,
  resendEmailOtp,
  resetPassword,
  verifyEmailOtp,
  sendBannerMessage,
  sendMessage,
  signIn,
  signUp,
  getAllUsers,
  getMessage,
  deleteUser,
  generateCredentials,
  activateUser,
  deactivateUser,
} from "../api/auth";
import {
  SignInDetailsModel,
  SignUpDetailsModel,
  PublicSignUpModel,
  ResetPasswordDetailModel,
  ChangePasswordDetailModelAuth,
  LogoutFromAllDevicesModel,
  UserEmailModel,
  SignResponseModel,
} from "../types/auth";
import { BannerMessage, CustomResponse, UserData } from "../types/user";
import { NotificationModel } from "../components/modal/SendNotification";
import { handleApiError } from "../queries/api";

// ==========================================
// AUTHENTICATION MUTATIONS
// ==========================================

export function useLogin(): UseMutationResult<
  CustomResponse<SignResponseModel>,
  Error,
  SignInDetailsModel
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["auth", "login"],
    mutationFn: signIn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
    onError: (error: any) => {
      // LoginPage routes these cases to dedicated screens itself
      const code = error?.response?.data?.errorMessage;
      if (code === "EMAIL_NOT_VERIFIED" || code === "ACCOUNT_DEACTIVATED") return;
      toast.error(handleApiError(error));
    },
  });
}

export function useSignUp(): UseMutationResult<
  CustomResponse<SignResponseModel>,
  Error,
  SignUpDetailsModel
> {
  return useMutation({
    mutationKey: ["auth", "signup"],
    mutationFn: signUp,
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function usePublicSignUp(): UseMutationResult<
  CustomResponse<any>,
  Error,
  PublicSignUpModel
> {
  return useMutation({
    mutationKey: ["auth", "public-signup"],
    mutationFn: publicSignUp,
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useVerifyEmailOtp(): UseMutationResult<
  CustomResponse<any>,
  Error,
  { email: string; otp: string }
> {
  return useMutation({
    mutationKey: ["auth", "verify-email-otp"],
    mutationFn: verifyEmailOtp,
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useResendEmailOtp(): UseMutationResult<
  CustomResponse<any>,
  Error,
  string
> {
  return useMutation({
    mutationKey: ["auth", "resend-email-otp"],
    mutationFn: resendEmailOtp,
    onSuccess: () => {
      toast.success("A new verification code has been sent to your email.");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

// ==========================================
// PASSWORD MANAGEMENT MUTATIONS
// ==========================================

export function useForgotPassword(): UseMutationResult<
  CustomResponse<string>,
  Error,
  string
> {
  return useMutation({
    mutationKey: ["auth", "forgot-password"],
    mutationFn: forgotPassword,
    onSuccess: () => {
      toast.success("Password reset email sent successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useResetPassword(): UseMutationResult<
  CustomResponse<string>,
  Error,
  ResetPasswordDetailModel
> {
  return useMutation({
    mutationKey: ["auth", "reset-password"],
    mutationFn: resetPassword,
    onSuccess: () => {
      toast.success("Password reset successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useChangePassword(): UseMutationResult<
  CustomResponse<string>,
  Error,
  ResetPasswordDetailModel
> {
  return useMutation({
    mutationKey: ["auth", "change-password"],
    mutationFn: changePassword,
    onSuccess: () => {
      toast.success("Password changed successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useChangeNewPassword(): UseMutationResult<
  CustomResponse<string>,
  Error,
  ResetPasswordDetailModel
> {
  return useMutation({
    mutationKey: ["auth", "change-new-password"],
    mutationFn: changeNewPassword,
    onSuccess: () => {
      toast.success("Password updated successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useChangePasswordAuth(): UseMutationResult<
  CustomResponse<SignResponseModel>,
  Error,
  ChangePasswordDetailModelAuth
> {
  return useMutation({
    mutationKey: ["auth", "change-password-auth"],
    mutationFn: changePasswordAuth,
    onSuccess: () => {
      toast.success("Password changed successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

// ==========================================
// SESSION MANAGEMENT MUTATIONS
// ==========================================

export function useLogoutFromAllDevices(): UseMutationResult<
  CustomResponse<SignResponseModel>,
  Error,
  LogoutFromAllDevicesModel
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["auth", "logout-all-devices"],
    mutationFn: logOutFromAllDevices,
    onSuccess: () => {
      queryClient.clear();
      toast.success("Logged out from all devices successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

// ==========================================
// USER MANAGEMENT QUERIES & MUTATIONS
// ==========================================

export function useGetAllUsers(): UseQueryResult<
  CustomResponse<UserData[]>,
  Error
> {
  return useQuery({
    queryKey: ["users", "all"],
    queryFn: getAllUsers,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useDeleteUser(): UseMutationResult<
  CustomResponse<any>,
  Error,
  string
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["users", "delete"],
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", "all"] });
      toast.success("User deleted successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useGenerateCredentials(): UseMutationResult<
  CustomResponse<any>,
  Error,
  UserEmailModel
> {
  return useMutation({
    mutationKey: ["users", "generate-credentials"],
    mutationFn: generateCredentials,
    onSuccess: () => {
      toast.success("Credentials generated successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useActivateUser(): UseMutationResult<
  CustomResponse<any>,
  Error,
  UserEmailModel
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["users", "activate"],
    mutationFn: activateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", "all"] });
      toast.success("User activated successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useDeactivateUser(): UseMutationResult<
  CustomResponse<any>,
  Error,
  UserEmailModel
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["users", "deactivate"],
    mutationFn: deactivateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", "all"] });
      toast.success("User deactivated successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

// ==========================================
// MESSAGE & NOTIFICATION OPERATIONS
// ==========================================

export function useGetMessage(): UseQueryResult<any, Error> {
  return useQuery({
    queryKey: ["messages", "user"],
    queryFn: getMessage,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useSendMessage(): UseMutationResult<
  CustomResponse<SignResponseModel>,
  Error,
  NotificationModel
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["messages", "send"],
    mutationFn: sendMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      toast.success("Message sent successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}

export function useSendBannerMessage(): UseMutationResult<
  CustomResponse<SignResponseModel>,
  Error,
  BannerMessage
> {
  return useMutation({
    mutationKey: ["messages", "banner"],
    mutationFn: sendBannerMessage,
    onSuccess: () => {
      toast.success("Banner message updated successfully!");
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
}
